/** HTML / XSLT bodies for mock contracts and NDA, plus the visual signature stamp. */

export type StampInfo = {
  company: string;
  lastName: string;
  firstName: string;
  title: string;
  signedAt: string;
  serial: string;
  validFrom: string;
  validUntil: string;
};

export function encodeBase64(value: string) {
  return btoa(unescape(encodeURIComponent(value)));
}

export function decodeBase64(value: string) {
  return decodeURIComponent(escape(atob(value)));
}

function escapeXml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildSignatureStamp(stamp: StampInfo) {
  return `<section style="border-bottom: none; margin: 24px 0 8px; color: #3b4395; font-family: Arial, sans-serif;">
    <div style="border: 3px solid #3b4395; padding: 20px;">
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;">
            <div style="border-right: 1px solid #3b4395; padding-right: 5px; align-self: center;">
                <p style="margin: 0; padding: 0; line-height: 150%;">${escapeXml(stamp.company)}</p>
            </div>
            <div style="padding-left: 10px; align-self: center;">
                <p style="margin: 0; padding: 0; line-height: 150%;">${escapeXml(stamp.lastName)}</p>
                <p style="margin: 0; padding: 0; line-height: 150%;">${escapeXml(stamp.firstName)}</p>
                <p style="margin: 0; padding: 0; line-height: 150%;">${escapeXml(stamp.title)}</p>
            </div>
            <div style="border-top: 1px solid #3b4395; padding-top: 10px; grid-column: span 2;">
                <span style="font-weight: 600; text-transform: uppercase; font-size: 0.8rem; margin-bottom: 0.5rem;">Дата и время подписания</span>
                <p style="margin: 0; padding: 0; line-height: 150%;">${escapeXml(stamp.signedAt)}</p>
            </div>
            <div style="border-top: 1px solid #3b4395; padding-top: 10px; grid-column: span 2;">
                <span style="font-weight: 600; text-transform: uppercase; font-size: 0.8rem; margin-bottom: 0.5rem;">Сертификат: СЕРИЙНЫЙ НОМЕР, период действия</span>
                <p style="margin: 0; padding: 0; line-height: 150%;">${escapeXml(stamp.serial)}</p>
                <p style="margin: 0; padding: 0; line-height: 150%;">с ${escapeXml(stamp.validFrom)} по ${escapeXml(stamp.validUntil)}</p>
            </div>
        </div>
    </div>
</section>`;
}

export function applyStamp(markup: string, stamp: StampInfo) {
  const block = buildSignatureStamp(stamp);
  if (markup.includes('</body>')) {
    return markup.replace('</body>', `${block}</body>`);
  }
  return `${markup}${block}`;
}

type ContractFields = {
  number: string;
  date: string;
  place: string;
  subject: string;
  /** Заголовок документа: у каждого типа договора он свой. */
  title?: string;
  owner: string;
  partner: string;
  stamps?: StampInfo[];
};

export function buildContractHtml({
  number,
  date,
  place,
  subject,
  title = 'Договор поставки нефтепродуктов',
  owner,
  partner,
  stamps = [],
}: ContractFields) {
  const stampHtml = stamps.map((stamp) => buildSignatureStamp(stamp)).join('');
  return `<!DOCTYPE html><html lang="ru"><head><meta charset="UTF-8" /><title>Договор ${escapeXml(number)}</title></head>
<body style="font-family: 'Times New Roman', Times, serif; color: #1a1a1a; max-width: 720px; margin: 0 auto; padding: 32px 24px; line-height: 1.45;">
  <h1 style="text-align: center; font-size: 22px; margin-bottom: 8px;">${escapeXml(title)}</h1>
  <p style="text-align: center; margin-top: 0;">№ ${escapeXml(number)} от ${escapeXml(date)}</p>
  <p style="text-align: right;">г. ${escapeXml(place)}</p>
  <p>${escapeXml(owner)} («Поставщик») и ${escapeXml(partner)} («Покупатель») заключили настоящий договор о нижеследующем.</p>
  <h2 style="font-size: 16px;">1. Предмет договора</h2>
  <p>1.1. Поставщик обязуется поставить, а Покупатель принять и оплатить: ${escapeXml(subject)}.</p>
  <h2 style="font-size: 16px;">2. Общие положения</h2>
  <p>2.1. Документ сформирован офлайн-моком для проверки флоу создания, превью и подписи.</p>
  <p>2.2. После подписания в конец документа добавляется визуальный штамп сертификата.</p>
  ${stampHtml}
</body></html>`;
}

type NdaFields = {
  number: string;
  date: string;
  location: string;
  durationYears: number;
  owner: string;
  partner: string;
  stamps?: StampInfo[];
};

export function buildNdaXslt({
  number,
  date,
  location,
  durationYears,
  owner,
  partner,
  stamps = [],
}: NdaFields) {
  const stampHtml = stamps.map((stamp) => buildSignatureStamp(stamp)).join('');
  return `<?xml version="1.0" encoding="utf-8" standalone="no"?>
<?xml-stylesheet type="text/xsl" href="#stylesheet"?>
<!DOCTYPE ON_NDA [
    <!ELEMENT xsl:stylesheet (#PCDATA)>
    <!ATTLIST xsl:stylesheet id ID #REQUIRED>
]>
<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
    xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
    <soapenv:Header></soapenv:Header>
    <soapenv:Body>
        <Visualization wsu:Id="visualization">
          <xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform"
                xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
                id="stylesheet" version="1.0">
                <xsl:output method="html" />
                <xsl:template match="soapenv:Envelope">
                    <html lang="ru">
                        <head>
                            <meta charset="UTF-8" />
                        </head>
                        <body style="font-family: 'Times New Roman', Times, serif; color: #1a1a1a; max-width: 720px; margin: 0 auto; padding: 32px 24px; line-height: 1.45;">
                           <h1 style="text-align: center; font-size: 22px;">Соглашение о неразглашении конфиденциальной информации</h1>
                           <p style="text-align: center;">№ ${escapeXml(number)} от ${escapeXml(date)}</p>
                           <p style="text-align: right;">${escapeXml(location)}</p>
                           <p>${escapeXml(owner)} и ${escapeXml(partner)} обязуются не разглашать конфиденциальную информацию, полученную в рамках работы в сервисе.</p>
                           <p>Срок действия соглашения: ${escapeXml(String(durationYears))} ${durationYears === 1 ? 'год' : 'лет'} с даты подписания.</p>
                           <p>Документ сформирован офлайн-моком. Подпись вставляет визуальный штамп сертификата.</p>
                           ${stampHtml}
                        </body>
                    </html>
                </xsl:template>
            </xsl:stylesheet>
        </Visualization>
    </soapenv:Body>
</soapenv:Envelope>`;
}

export function buildNdaHtml(fields: NdaFields) {
  const stampHtml = (fields.stamps ?? []).map((stamp) => buildSignatureStamp(stamp)).join('');
  return `<!DOCTYPE html><html lang="ru"><head><meta charset="UTF-8" /></head>
<body style="font-family: 'Times New Roman', Times, serif; max-width: 720px; margin: 0 auto; padding: 32px 24px;">
  <h1 style="text-align: center;">Соглашение о неразглашении</h1>
  <p style="text-align: center;">№ ${escapeXml(fields.number)} от ${escapeXml(fields.date)}</p>
  <p>${escapeXml(fields.owner)} и ${escapeXml(fields.partner)}. Срок: ${escapeXml(String(fields.durationYears))} лет. Место: ${escapeXml(fields.location)}.</p>
  ${stampHtml}
</body></html>`;
}

type PoaFields = {
  number: string;
  issueDate: string;
  expirationDate: string;
  principal: string;
  representative: string;
  powers: string[];
};

/**
 * XML машиночитаемой доверенности со встроенной XSL-таблицей — ровно та форма,
 * которую разбирает `processXslt` на экране предпросмотра МЧД.
 */
export function buildPoaXslt({
  number,
  issueDate,
  expirationDate,
  principal,
  representative,
  powers,
}: PoaFields) {
  const powerRows = powers
    .map((power) => `<li style="margin-bottom: 6px;">${escapeXml(power)}</li>`)
    .join('');

  return `<?xml version="1.0" encoding="utf-8" standalone="no"?>
<?xml-stylesheet type="text/xsl" href="#stylesheet"?>
<!DOCTYPE Doverennost [
    <!ELEMENT xsl:stylesheet (#PCDATA)>
    <!ATTLIST xsl:stylesheet id ID #REQUIRED>
]>
<Doverennost xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" number="${escapeXml(number)}">
    <xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform" id="stylesheet" version="1.0">
        <xsl:output method="html" />
        <xsl:template match="Doverennost">
            <html lang="ru">
                <head>
                    <meta charset="UTF-8" />
                </head>
                <body style="font-family: 'Times New Roman', Times, serif; color: #1a1a1a; max-width: 720px; margin: 0 auto; padding: 32px 24px; line-height: 1.45;">
                    <h1 style="text-align: center; font-size: 22px;">Машиночитаемая доверенность</h1>
                    <p style="text-align: center;">№ ${escapeXml(number)} от ${escapeXml(issueDate)}</p>
                    <p><b>Доверитель:</b> ${escapeXml(principal)}</p>
                    <p><b>Представитель:</b> ${escapeXml(representative)}</p>
                    <p><b>Срок действия:</b> по ${escapeXml(expirationDate)}</p>
                    <p><b>Полномочия:</b></p>
                    <ul>${powerRows}</ul>
                    <p>Документ сформирован офлайн-моком. Подпись добавляет визуальный штамп сертификата.</p>
                </body>
            </html>
        </xsl:template>
    </xsl:stylesheet>
</Doverennost>`;
}
